import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Paper,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from '../design-system';
import {
  CheckCircle,
  Warning,
  Block,
  Upload,
  ContentCopy,
  PlayArrow,
} from '@mui/icons-material';
import { ssoService } from '../../services/SsoService';
import * as dt from '../../../../shared/src/data.types';
import type {
  ConfigureSsoRequest,
  SamlInputMode,
} from '../../types/sso';
import {
  DEFAULT_SAML_ATTRIBUTE_MAPPING,
  SSO_ENFORCEMENT_DESCRIPTIONS,
} from '../../types/sso';

interface SsoConfigurationPanelProps {
  tenant: {
    id: string;
    name: string;
    subdomain: string;
    ssoEnabled?: boolean;
    ssoEnforcement?: 'disabled' | 'optional' | 'required';
    samlIdpEntityId?: string | null;
    samlIdpSsoUrl?: string | null;
    samlIdpCertificate?: string | null;
    ssoJitProvisioning?: boolean;
    ssoDefaultRole?: dt.ALL_USER_ROLES;
    samlAttributeMapping?: Record<string, string>;
    ssoConfiguredAt?: string | null;
  };
  onSsoUpdated: (tenant: SsoConfigurationPanelProps['tenant']) => void;
}

const SsoConfigurationPanel: React.FC<SsoConfigurationPanelProps> = ({
  tenant,
  onSsoUpdated,
}) => {
  // Form state
  const [ssoEnabled, setSsoEnabled] = useState(tenant.ssoEnabled ?? false);
  const [ssoEnforcement, setSsoEnforcement] = useState<'disabled' | 'optional' | 'required'>(
    tenant.ssoEnforcement ?? 'disabled'
  );
  const [inputMode, setInputMode] = useState<SamlInputMode>('manual');
  const [samlIdpEntityId, setSamlIdpEntityId] = useState(tenant.samlIdpEntityId ?? '');
  const [samlIdpSsoUrl, setSamlIdpSsoUrl] = useState(tenant.samlIdpSsoUrl ?? '');
  const [samlIdpCertificate, setSamlIdpCertificate] = useState(tenant.samlIdpCertificate ?? '');
  const [ssoJitProvisioning, setSsoJitProvisioning] = useState(tenant.ssoJitProvisioning ?? false);
  const [ssoDefaultRole, setSsoDefaultRole] = useState<dt.ALL_USER_ROLES>(
    tenant.ssoDefaultRole ?? dt.ALL_USER_ROLES.tenant_engineer
  );
  const [samlAttributeMapping, setSamlAttributeMapping] = useState<Record<string, string>>(
    tenant.samlAttributeMapping ?? DEFAULT_SAML_ATTRIBUTE_MAPPING
  );

  // UI state
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Update form when tenant changes
  useEffect(() => {
    setSsoEnabled(tenant.ssoEnabled ?? false);
    setSsoEnforcement(tenant.ssoEnforcement ?? 'disabled');
    setSamlIdpEntityId(tenant.samlIdpEntityId ?? '');
    setSamlIdpSsoUrl(tenant.samlIdpSsoUrl ?? '');
    setSamlIdpCertificate(tenant.samlIdpCertificate ?? '');
    setSsoJitProvisioning(tenant.ssoJitProvisioning ?? false);
    setSsoDefaultRole(tenant.ssoDefaultRole ?? dt.ALL_USER_ROLES.tenant_engineer);
    setSamlAttributeMapping(tenant.samlAttributeMapping ?? DEFAULT_SAML_ATTRIBUTE_MAPPING);
  }, [tenant]);

  const handleSsoEnabledToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSsoEnabled(event.target.checked);
    if (!event.target.checked) {
      setSsoEnforcement('disabled');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const config: ConfigureSsoRequest = {
        ssoEnabled,
        ssoEnforcement,
        ...(ssoEnabled && {
          samlIdpEntityId: samlIdpEntityId || undefined,
          samlIdpSsoUrl: samlIdpSsoUrl || undefined,
          samlIdpCertificate: samlIdpCertificate || undefined,
          ssoJitProvisioning,
          ssoDefaultRole,
          samlAttributeMapping,
        }),
      };

      const response = await ssoService.configureSso(tenant.id, config);
      setSuccess(response.message);
      onSsoUpdated(response.tenant);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to configure SSO');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await ssoService.testSsoConnection(tenant.id);
      if (response.success) {
        setSuccess(response.message);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test SSO connection');
    } finally {
      setTesting(false);
    }
  };

  const handleCopyCallbackUrl = () => {
    // In production, this would be the actual Cognito callback URL
    const callbackUrl = `https://${tenant.subdomain}.yourdomain.com/auth/callback`;
    navigator.clipboard.writeText(callbackUrl);
    setSuccess('Callback URL copied to clipboard');
  };

  const getStatusIcon = () => {
    if (!ssoEnabled) {
      return <Block color="disabled" />;
    }
    const isConfigured = samlIdpEntityId && samlIdpSsoUrl && samlIdpCertificate;
    if (isConfigured) {
      return <CheckCircle color="success" />;
    }
    return <Warning color="warning" />;
  };

  const getStatusText = () => {
    if (!ssoEnabled) {
      return 'SSO Disabled';
    }
    const isConfigured = samlIdpEntityId && samlIdpSsoUrl && samlIdpCertificate;
    if (isConfigured) {
      return 'SSO Configured';
    }
    return 'SSO Partially Configured';
  };

  return (
    <Box>
      {/* Status Section */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {getStatusIcon()}
          <Box flexGrow={1}>
            <Typography variant="h6">{getStatusText()}</Typography>
            {tenant.ssoConfiguredAt && (
              <Typography variant="caption" color="textSecondary">
                Last configured: {new Date(tenant.ssoConfiguredAt).toLocaleString()}
              </Typography>
            )}
          </Box>
          <FormControlLabel
            control={<Switch checked={ssoEnabled} onChange={handleSsoEnabledToggle} />}
            label="Enable SSO"
          />
        </Stack>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* SSO Configuration Form */}
      {ssoEnabled && (
        <Stack spacing={3}>
          {/* Enforcement Mode */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                SSO Enforcement Mode
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Enforcement</InputLabel>
                <Select
                  value={ssoEnforcement}
                  onChange={(e) => setSsoEnforcement(e.target.value as 'disabled' | 'optional' | 'required')}
                  label="Enforcement"
                >
                  <MenuItem value="disabled">Disabled</MenuItem>
                  <MenuItem value="optional">Optional</MenuItem>
                  <MenuItem value="required">Required</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                {SSO_ENFORCEMENT_DESCRIPTIONS[ssoEnforcement]}
              </Typography>
            </CardContent>
          </Card>

          {/* SAML Configuration */}
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">SAML Configuration</Typography>
                <ToggleButtonGroup
                  value={inputMode}
                  exclusive
                  onChange={(_, value) => value && setInputMode(value)}
                  size="small"
                >
                  <ToggleButton value="manual">Manual Entry</ToggleButton>
                  <ToggleButton value="metadata">Upload Metadata</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {inputMode === 'manual' ? (
                <Stack spacing={2}>
                  <AppTextField
                    fullWidth
                    label="IdP Entity ID"
                    value={samlIdpEntityId}
                    onChange={(e) => setSamlIdpEntityId(e.target.value)}
                    placeholder="https://idp.example.com/entity"
                    helperText="The unique identifier for your identity provider"
                  />
                  <AppTextField
                    fullWidth
                    label="SSO URL"
                    value={samlIdpSsoUrl}
                    onChange={(e) => setSamlIdpSsoUrl(e.target.value)}
                    placeholder="https://idp.example.com/sso"
                    helperText="The SAML SSO endpoint URL"
                  />
                  <AppTextField
                    fullWidth
                    label="X.509 Certificate"
                    value={samlIdpCertificate}
                    onChange={(e) => setSamlIdpCertificate(e.target.value)}
                    multiline
                    rows={4}
                    placeholder="-----BEGIN CERTIFICATE-----..."
                    helperText="The public certificate from your identity provider"
                  />
                </Stack>
              ) : (
                <Box textAlign="center" py={4}>
                  <AppButton variant="outlined" startIcon={<Upload />} component="label">
                    Upload SAML Metadata XML
                    <input type="file" accept=".xml" hidden />
                  </AppButton>
                  <Typography variant="caption" display="block" mt={1} color="textSecondary">
                    Upload your IdP's metadata XML file to auto-populate fields
                  </Typography>
                </Box>
              )}

              {/* Callback URL */}
              <Box mt={2} p={2} bgcolor="background.default" borderRadius={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" color="textSecondary">
                    Callback URL (for IdP configuration):
                  </Typography>
                  <Typography variant="caption" fontFamily="monospace">
                    https://{tenant.subdomain}.yourdomain.com/auth/callback
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <AppIconButton size="small" onClick={handleCopyCallbackUrl}>
                      <ContentCopy fontSize="small" />
                    </AppIconButton>
                  </Tooltip>
                </Stack>
              </Box>
            </CardContent>
          </Card>

          {/* JIT Provisioning */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Just-In-Time (JIT) Provisioning
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={ssoJitProvisioning}
                    onChange={(e) => setSsoJitProvisioning(e.target.checked)}
                  />
                }
                label="Enable JIT Provisioning"
              />
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Automatically create user accounts on first SSO login
              </Typography>

              {ssoJitProvisioning && (
                <FormControl fullWidth>
                  <InputLabel>Default Role for New Users</InputLabel>
                  <Select
                    value={ssoDefaultRole}
                    onChange={(e) => setSsoDefaultRole(e.target.value as dt.ALL_USER_ROLES)}
                    label="Default Role for New Users"
                  >
                    <MenuItem value={dt.ALL_USER_ROLES.tenant_admin}>
                      {dt.getRoleDisplayName(dt.ALL_USER_ROLES.tenant_admin)}
                    </MenuItem>
                    <MenuItem value={dt.ALL_USER_ROLES.tenant_lead}>
                      {dt.getRoleDisplayName(dt.ALL_USER_ROLES.tenant_lead)}
                    </MenuItem>
                    <MenuItem value={dt.ALL_USER_ROLES.tenant_engineer}>
                      {dt.getRoleDisplayName(dt.ALL_USER_ROLES.tenant_engineer)}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            </CardContent>
          </Card>

          {/* Attribute Mapping */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                SAML Attribute Mapping
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Map SAML assertion attributes to user profile fields
              </Typography>
              <Stack spacing={2}>
                <AppTextField
                  fullWidth
                  label="Email Attribute"
                  value={samlAttributeMapping.email || ''}
                  onChange={(e) =>
                    setSamlAttributeMapping({ ...samlAttributeMapping, email: e.target.value })
                  }
                  placeholder="http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
                />
                <AppTextField
                  fullWidth
                  label="First Name Attribute"
                  value={samlAttributeMapping.firstName || ''}
                  onChange={(e) =>
                    setSamlAttributeMapping({ ...samlAttributeMapping, firstName: e.target.value })
                  }
                  placeholder="http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
                />
                <AppTextField
                  fullWidth
                  label="Last Name Attribute"
                  value={samlAttributeMapping.lastName || ''}
                  onChange={(e) =>
                    setSamlAttributeMapping({ ...samlAttributeMapping, lastName: e.target.value })
                  }
                  placeholder="http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Actions */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <AppButton
              variant="outlined"
              onClick={handleTestConnection}
              disabled={loading || testing || !samlIdpEntityId || !samlIdpSsoUrl}
              startIcon={testing ? <CircularProgress size={16} /> : <PlayArrow />}
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </AppButton>
            <AppButton
              variant="contained"
              onClick={handleSave}
              disabled={loading || testing}
              startIcon={loading ? <CircularProgress size={16} /> : undefined}
            >
              {loading ? 'Saving...' : 'Save Configuration'}
            </AppButton>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default SsoConfigurationPanel;
